import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Grid,
  Group,
  Paper,
  Select,
  SimpleGrid,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconCalendarDollar,
  IconMoneybag,
  IconWallet,
} from "@tabler/icons-react";

import { WithDashboardNavbar } from "@layouts/WithDashboardNavbar";
import { WithSidebar } from "@layouts/WithSidebar";

import { PayoutServicesTable } from "@components/PayoutServicesTable";

import { DEFAULT_PAGINATION } from "@constants/index";

import { useGetEarningsByServices } from "@hooks/queries/dashboard/useGetEarningsByServices";
import { useGetMyTier } from "@hooks/queries/earnings/useGetMyTier";

import { DateRangeSelect } from "../../AdminDashboard/components/DateRangeSelect";
import { EarningsOverviewCard } from "../../AdminDashboard/components/EarningsOverviewCard";

import { RequestPayoutModal } from "./components/RequestPayoutModal";
import { TierProgressCard } from "./components/TierProgressCard";


export const StaffEarningsPage = () => {
  const { t } = useTranslation();

  const [dateRange, setDateRange] = useState<string | null>("last-30-days");
  const [customDateRange, setCustomDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [servicesCurrentPage, setServicesCurrentPage] = useState(1);
  
  // Filters
  const [billStatus, setBillStatus] = useState<string | null>(null);
  const [reportStatus, setReportStatus] = useState<string | null>(null);
  const [payoutStatus, setPayoutStatus] = useState<string | null>(null);

  const [requestPayoutModalOpened, { open: openRequestPayoutModal, close: closeRequestPayoutModal }] = useDisclosure(false);

  const { 
    data: tierData, 
    isLoading: isTierLoading 
  } = useGetMyTier();

  const {
    data: servicesData,
    isLoading: isServicesLoading,
    isError: isServicesError,
    isSuccess: isServicesSuccess,
  } = useGetEarningsByServices({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && {
        startDate: customDateRange[0]?.toISOString().split('T')[0],
        endDate: customDateRange[1]?.toISOString().split('T')[0],
    }),
    pagination: {
      page: servicesCurrentPage,
      pageSize: DEFAULT_PAGINATION.pageSize,
    },
    billStatus: billStatus === "all" ? undefined : billStatus || undefined,
    reportStatus: reportStatus === "all" ? undefined : reportStatus || undefined,
    payoutStatus: payoutStatus === "all" ? undefined : payoutStatus || undefined,
  });

  const availableAmount = servicesData?.totals?.totalOutstandingPayout || 0;

  return (
    <WithSidebar hasDashboardNavbar hasBackground={false}>
      <WithDashboardNavbar userRole="staff">
        
        <RequestPayoutModal 
          opened={requestPayoutModalOpened} 
          onClose={closeRequestPayoutModal}
          availableAmount={availableAmount}
        />

        <Group mt={"1rem"} justify="space-between" align="flex-end">
          <Group justify="start">
            <Text fz={15}>{t("displaying-stats-for")}</Text>
            <DateRangeSelect
              dateRange={dateRange}
              setDateRange={setDateRange}
              customDateRange={customDateRange}
              setCustomDateRange={setCustomDateRange}
            />
          </Group>
          
          <Box>
            <Tooltip
              label={t("feature-coming-soon", "Feature coming soon")}
              withArrow
            >
              <Box style={{ display: 'inline-block' }}>
                <Button 
                  color="green" 
                  onClick={openRequestPayoutModal}
                  disabled
                  leftSection={<IconWallet size={18} />}
                >
                  {t("request-payout")}
                </Button>
              </Box>
            </Tooltip>
          </Box>
        </Group>

        <Grid mt={"2rem"} gutter="lg">
          <Grid.Col span={{ base: 12, md: 8 }}>
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
              <EarningsOverviewCard
                title={t("available-to-request")}
                amount={availableAmount}
                isLoading={isServicesLoading}
                amountPostfix="CFA"
                icon={
                  <Group
                    bg="green.0"
                    w={30}
                    h={30}
                    justify="center"
                    style={{ borderRadius: "var(--border-radius-1)" }}>
                    <IconWallet
                      size={20}
                      stroke={1.5}
                      color="var(--mantine-color-green-6)"
                    />
                  </Group>
                }
              />
              <EarningsOverviewCard
                title={t("total-earned")}
                amount={servicesData?.totals?.totalAmountDueToBePaidOut || 0}
                isLoading={isServicesLoading}
                amountPostfix="CFA"
                icon={
                  <Group
                    bg="blue.0"
                    w={30}
                    h={30}
                    justify="center"
                    style={{ borderRadius: "var(--border-radius-1)" }}>
                    <IconMoneybag
                      size={20}
                      stroke={1.5}
                      color="var(--mantine-color-blue-6)"
                    />
                  </Group>
                }
              />
              <EarningsOverviewCard
                title={t("processing-payout")}
                amount={servicesData?.totals?.totalProcessingPayout || 0}
                isLoading={isServicesLoading}
                amountPostfix="CFA"
                icon={
                  <Group
                    bg="orange.0"
                    w={30}
                    h={30}
                    justify="center"
                    style={{ borderRadius: "var(--border-radius-1)" }}>
                    <IconCalendarDollar
                      size={20}
                      stroke={1.5}
                      color="var(--mantine-color-orange-6)"
                    />
                  </Group>
                }
              />
              <EarningsOverviewCard
                title={t("pending-report-completion")}
                amount={servicesData?.totals?.totalPendingReportCompletion || 0}
                isLoading={isServicesLoading}
                amountPostfix="CFA"
                icon={
                  <Group
                    bg="red.0"
                    w={30}
                    h={30}
                    justify="center"
                    style={{ borderRadius: "var(--border-radius-1)" }}>
                    <IconCalendarDollar
                      size={20}
                      stroke={1.5}
                      color="var(--mantine-color-red-6)"
                    />
                  </Group>
                }
              />
            </SimpleGrid>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <TierProgressCard tierInfo={tierData} isLoading={isTierLoading} />
          </Grid.Col>
        </Grid>

        <Grid mt="3rem" mb="8rem" gutter={"lg"}>
          <Grid.Col span={{ base: 12 }}>
            <Paper withBorder p="md" w={{ base: "fit-content", md: "100%" }}>
              <Group justify="space-between" mb="md">
                <Title order={3} fz={17} fw={500}>
                  {t("detailed-services")}
                </Title>
              </Group>

              <Group mb="md" gap="md" align="flex-end">
                <Group gap="xs">
                  <Text fz={14} fw={500}>{t("bill-status")}:</Text>
                  <Select
                    maw={150}
                    data={[
                      { label: t("all"), value: "all" },
                      { label: t("paid"), value: "paid" },
                      { label: t("pending"), value: "pending" },
                    ]}
                    value={billStatus}
                    onChange={(val) => {
                      setBillStatus(val);
                      setServicesCurrentPage(1);
                    }}
                    clearable
                  />
                </Group>
                
                <Group gap="xs">
                  <Text fz={14} fw={500}>{t("report-status")}:</Text>
                  <Select
                    maw={150}
                    data={[
                      { label: t("all"), value: "all" },
                      { label: t("complete"), value: "complete" },
                      { label: t("incomplete"), value: "incomplete" },
                    ]}
                    value={reportStatus}
                    onChange={(val) => {
                      setReportStatus(val);
                      setServicesCurrentPage(1);
                    }}
                    clearable
                  />
                </Group>

                <Group gap="xs">
                  <Text fz={14} fw={500}>{t("payout-status")}:</Text>
                  <Select
                    maw={150}
                    data={[
                      { label: t("all"), value: "all" },
                      { label: t("completed"), value: "completed" },
                      { label: t("pending"), value: "pending" },
                      { label: t("processing"), value: "processing" },
                      { label: t("failed"), value: "failed" },
                    ]}
                    value={payoutStatus}
                    onChange={(val) => {
                      setPayoutStatus(val);
                      setServicesCurrentPage(1);
                    }}
                    clearable
                  />
                </Group>
              </Group>

              <PayoutServicesTable 
                  data={servicesData?.newData || null}
                  isLoading={isServicesLoading}
                  isError={isServicesError}
                  isSuccess={isServicesSuccess}
                  pagination={servicesData?.pagination || {
                      page: servicesCurrentPage,
                      pageSize: DEFAULT_PAGINATION.pageSize,
                      pageCount: 1,
                      total: 0
                  }}
                  onPageChange={setServicesCurrentPage}
                  userRole="staff"
                  columnCount={7}
              />
            </Paper>
          </Grid.Col>
        </Grid>
        
      </WithDashboardNavbar>
    </WithSidebar>
  );
};
